import { post, get, put } from './api.service';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY } from '../utils/constants';
import { User, AuthTokens, LoginCredentials, UserRole, SendOTPInput, SignupWithOTPInput } from '../types';

interface LoginResponse {
  user: User;
  tokens: AuthTokens;
}

interface RefreshResponse {
  tokens: AuthTokens;
}

interface UserResponse {
  user: User;
  message?: string;
}

interface SendOTPResponse {
  message: string;
  role: UserRole;
}

interface SignupWithOTPResponse {
  user: User;
  tokens: AuthTokens;
  message: string;
}

interface UpdateProfileInput {
  name?: string;
}

// ============================================================================
// AUTH SERVICE
// ============================================================================

export const authService = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await post<LoginResponse>('/auth/login', credentials);

    // Store tokens
    localStorage.setItem(TOKEN_KEY, response.tokens.accessToken);
    if (response.tokens.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, response.tokens.refreshToken);
    }
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));

    return response;
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await post('/auth/logout');
    } finally {
      // Always clear local storage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }
  },

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<AuthTokens> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await post<RefreshResponse>('/auth/refresh', { refreshToken });
    localStorage.setItem(TOKEN_KEY, response.tokens.accessToken);

    return response.tokens;
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    const response = await get<UserResponse>('/auth/me');
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));
    return response.user;
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Get stored user from local storage
   */
  getStoredUser(): User | null {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  /**
   * Get access token
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Clear all auth data
   */
  clearAuth(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  // ============================================================================
  // OTP-BASED AUTHENTICATION
  // ============================================================================

  /**
   * Send OTP to email for signup or login
   */
  async sendOTP(input: SendOTPInput): Promise<SendOTPResponse> {
    return post<SendOTPResponse>('/auth/send-otp', input);
  },

  /**
   * Signup with OTP verification
   */
  async signupWithOTP(input: SignupWithOTPInput): Promise<SignupWithOTPResponse> {
    const response = await post<SignupWithOTPResponse>('/auth/signup-with-otp', input);

    // Store tokens
    localStorage.setItem(TOKEN_KEY, response.tokens.accessToken);
    if (response.tokens.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, response.tokens.refreshToken);
    }
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));

    return response;
  },

  /**
   * Login with OTP (passwordless)
   */
  async loginWithOTP(email: string, otp: string): Promise<LoginResponse> {
    const response = await post<LoginResponse>('/auth/login-with-otp', {
      email,
      otp,
      purpose: 'login',
    });

    // Store tokens
    localStorage.setItem(TOKEN_KEY, response.tokens.accessToken);
    if (response.tokens.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, response.tokens.refreshToken);
    }
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));

    return response;
  },

  /**
   * Update user profile
   */
  async updateProfile(input: UpdateProfileInput): Promise<UserResponse> {
    const response = await put<UserResponse>('/auth/profile', input);

    // Update stored user data
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));

    return response;
  },

  /**
   * Change password for authenticated user. Backend endpoint already exists at
   * PUT /auth/change-password (verifies currentPassword against bcrypt hash,
   * enforces newPassword ≥ 6 chars).
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return put<{ message: string }>('/auth/change-password', { currentPassword, newPassword });
  },
};

export default authService;
